import {
  Alerter,
  Button,
  Callout,
  Card,
  CardLevel,
  Col,
  Drawer,
  Frow,
  Heading2,
  Intent,
  PaddingLevel,
} from '@pinpointhq/thumbtack';
import * as React from 'react';
import { visit } from 'turbolinks';
import { exportAdminAllocationRunPath } from '../../../javascript/application/ts_routes';
import { AllocationRun, IGeneralAllocationSettings } from '../../../javascript/models';
import { createToast } from '../../FlashToaster';
import { DrawerFooter } from '../../shared/DrawerFooter';
import { DrawerHeader } from '../../shared/DrawerHeader';
import humanize from '../../shared/utilities/humanize';

const SUMMARY_LABELS = [
  ['allocations', 'Allocations'],
  ['candidates', 'Eligible candidates'],
  ['candidates_allocated', 'Candidates allocated'],
  ['candidates_without_allocation', 'Candidates with no allocation'],
  ['jobs', 'Jobs included'],
  ['jobs_without_allocation', 'Jobs with no allocation'],
  ['jobs_below_target', 'Jobs below the top-up target'],
];

// A finished general allocation: what it produced, what it was run with, the CSV, and assigning it
export function GeneralShowDrawer({
  allocationRun,
  fetchData,
  handleClose,
  handleRunAgain,
}: {
  allocationRun: AllocationRun;
  fetchData: () => void;
  handleClose: () => void;
  handleRunAgain: (settings: IGeneralAllocationSettings) => void;
}) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const general = (allocationRun.output || {}).general || {};
  const summary = general.summary || {};
  const settings = allocationRun.settings;

  const handleDownload = () => visit(exportAdminAllocationRunPath(allocationRun.id, { job_id: 'general' }));

  const handleAssign = () => {
    Alerter.create({
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Assign',
      intent: Intent.DANGER,
      onConfirm: () => {
        setIsSubmitting(true);
        allocationRun.assign = true;
        allocationRun.save().then((success) => {
          if (success) {
            createToast({ text: 'Assigning. Applications are being created and sent to Pinpoint.', type: 'success' });
            fetchData();
            handleClose();
          } else {
            setIsSubmitting(false);
            allocationRun.assign = false;
            const message = allocationRun.errors.base ? allocationRun.errors.base.message : 'Unable to assign allocation';
            createToast({ text: message, type: 'error' });
          }
        });
      },
      text: `This creates ${summary.allocations} applications and sends them to the employers' Pinpoint accounts. It can't be undone from here.`,
    });
  };

  const handleRetryFailed = () => {
    allocationRun.retryFailed = true;
    allocationRun.save().then((success) => {
      allocationRun.retryFailed = false;
      createToast({
        text: success ? 'Sending the failed applications to Pinpoint again' : 'Unable to retry',
        type: success ? 'success' : 'error',
      });
      fetchData();
    });
  };

  const assignResult = (allocationRun.output || {}).assign;
  const pinpointCounts = allocationRun.pinpointCounts;

  const settingRows: Array<[string, string]> = settings
    ? [
        ['Preference passes', settings.preferences.length ? settings.preferences.map(humanize).join(', ') : 'None (top up only)'],
        ['Fall back to other preferences', settings.fall_back_to_other_preferences ? 'Yes' : 'No'],
        ['Match region', settings.match_region ? 'Yes' : 'No'],
        ['Widen to neighbouring regions', settings.expand_regions ? 'Yes' : 'No'],
        ['Widen to related sectors', settings.expand_workstreams ? 'Yes' : 'No'],
        ['Membership levels', settings.membership_statuses.map(humanize).join(', ')],
        ['Job stages', settings.job_progress.map(humanize).join(', ')],
        ['Large jobs', `${settings.large_job_headcount}+ places, at most ${settings.large_job_max_per_headcount} per place`],
        [
          'Top up',
          settings.top_up
            ? `To ${settings.top_up_per_headcount} per place, candidates with fewer than ${settings.max_allocations_per_candidate} allocations`
            : 'No',
        ],
        ['Candidate conditions', settings.conditions.length ? String(settings.conditions.length) : 'None'],
      ]
    : [];

  return (
    <Drawer maxWidth={800} isOpen={true} onClose={handleClose}>
      <DrawerHeader title={general.title || 'General allocation'} handleClose={handleClose} />
      <Card paddingLevel={PaddingLevel.EXPANDED} level={CardLevel.NONE} style={{ overflowY: 'scroll' }}>
        {allocationRun.status === 'assigning' && (
          <Callout intent={Intent.PRIMARY} className="mar-b-4">
            Assigning: applications are being created and sent to employers' Pinpoint accounts.
          </Callout>
        )}
        {assignResult && assignResult.error && (
          <Callout intent={Intent.DANGER} className="mar-b-4">
            Assigning stopped with an error: {assignResult.error}. Applications already created are kept, and
            assigning again carries on without duplicating them.
          </Callout>
        )}
        {allocationRun.status === 'assigned' && assignResult && (
          <Callout intent={Intent.SUCCESS} className="mar-b-4">
            Assigned. {assignResult.created || 0} applications created.
            {assignResult.skipped_offered_or_hired
              ? ` ${assignResult.skipped_offered_or_hired} skipped because the candidate now has an offer or hire.`
              : ''}
            {assignResult.skipped_already_at_company
              ? ` ${assignResult.skipped_already_at_company} skipped because the candidate already has an application at that organisation.`
              : ''}
          </Callout>
        )}
        {pinpointCounts && pinpointCounts.posted + pinpointCounts.failed + pinpointCounts.pending > 0 && (
          <Callout intent={pinpointCounts.failed ? Intent.WARNING : Intent.NONE} className="mar-b-4">
            In Pinpoint: {pinpointCounts.posted} sent, {pinpointCounts.pending} waiting, {pinpointCounts.failed} failed.
            {pinpointCounts.failed > 0 && (
              <Button className="mar-l-2" small={true} text="Retry failed" onClick={handleRetryFailed} />
            )}
          </Callout>
        )}

        <Heading2>Results</Heading2>
        <Frow gutters={16} verticalGutters={16} className="mar-b-4">
          {SUMMARY_LABELS.map(([key, label]) => (
            <Col key={key} md={{ width: '1-3' }}>
              <Card paddingLevel={PaddingLevel.REGULAR} level={CardLevel.OUTLINED}>
                <div className="bp3-text-muted">{label}</div>
                <div style={{ fontSize: 24 }}>{summary[key] === undefined || summary[key] === null ? '-' : summary[key]}</div>
              </Card>
            </Col>
          ))}
        </Frow>
        <Button text="Download CSV" onClick={handleDownload} className="mar-b-4" />

        <Heading2>Settings used</Heading2>
        <table className="table table--full table--condensed">
          <tbody>
            {settingRows.map(([label, value]) => (
              <tr key={label}>
                <td>{label}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <DrawerFooter>
        <Frow justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
          <div>
            {settings && <Button minimal={true} text="Change settings and run again" onClick={() => handleRunAgain(settings)} />}
          </div>
          {allocationRun.status === 'completed' && (
            <div>
              <Button text="Assign" intent={Intent.PRIMARY} onClick={handleAssign} disabled={isSubmitting} />
            </div>
          )}
        </Frow>
      </DrawerFooter>
    </Drawer>
  );
}
